/**
 * The CRM «Prezzo barrato» in the app (client, 27/10/2026).
 *
 * The store price is the real one, charged at purchase AND at every renewal.
 * The CRM can add a higher reference price per App Store / Google Play
 * product, shown crossed out next to it with the saving (e.g. 42,99 € → 29,99 €,
 * -30%), on the paywall and in the Shop. It's never charged, and it's shown
 * only when the store has no offer of its own and the reference is higher than
 * what the store actually charges.
 *
 * Pure functions, no app imports, so the paywall, the Shop price map and the
 * tests share them.
 */

const MERCHANT_BY_PLATFORM = {
  ios: "apple_store",
  android: "google_play",
};

/** Store SKU without Google's `:basePlan` suffix, lower-case. */
export const skuOf = (identifier) =>
  String(identifier || "")
    .trim()
    .toLowerCase()
    .split(":")[0];

/**
 * Rows of `GET /api/selling-subscription/list` → { sku: reference } for this
 * platform's store rows. Rows without a reference (null, 0) are left out.
 */
export const referencePricesFromRows = (rows, platform) => {
  const merchant = MERCHANT_BY_PLATFORM[platform];
  if (!merchant || !Array.isArray(rows)) return {};

  const map = {};
  for (const row of rows) {
    if (String(row?.merchant?.mnemonic || "").toLowerCase() !== merchant) continue;

    const sku = skuOf(row?.merchant_product_id || row?.sku);
    const reference = Number(row?.reference_price);
    if (!sku || !Number.isFinite(reference) || reference <= 0) continue;

    map[sku] = reference;
  }

  return map;
};

/**
 * The crossed-out display for a store product, or null.
 *
 * `pricing` is `normalizeProductPricing()`'s result. A real store offer (intro
 * price the member can get) always wins: then this returns null and the caller
 * keeps the offer display.
 *
 * @returns {{ reference: number, price: number, saving: number, percent: number } | null}
 */
export const withReferencePrice = (pricing, reference) => {
  const ref = Number(reference);
  const price = Number(pricing?.priceFull);

  if (!pricing || pricing.hasOffer) return null;
  if (!Number.isFinite(ref) || !Number.isFinite(price) || price <= 0) return null;
  if (ref <= price) return null;

  const saving = Math.round((ref - price) * 100) / 100;

  return {
    reference: ref,
    price,
    saving,
    percent: Math.round((saving / ref) * 100),
  };
};
